import { computed, onUnmounted, ref, type Ref, watch } from 'vue'

export type Breakpoint = 'sm' | 'lg'

type UseBreakpoints = Record<Breakpoint, Ref<boolean>> & {
    initialized: Ref<boolean>
    current: Ref<Breakpoint>
}

let instance: UseBreakpoints | null = null

export const DEFAULT_SM_BREAKPOINT = 768

const forcedBreakpoint = ref<Breakpoint | undefined>()

export const setForcedBreakpoint = (breakpoint?: Breakpoint) => {
    forcedBreakpoint.value = breakpoint
}

export const installBreakpoints = (widgetRef: Ref<HTMLElement | undefined>) => {
    const initialized = ref(false)

    const sm = ref(true)
    const lg = ref(false)

    const current = computed<Breakpoint>(() => sm.value ? 'sm' : 'lg')

    const resizeObserver = new ResizeObserver(() => update())

    const update = (param?: Breakpoint) => {
        if (param) {
            forcedBreakpoint.value = param
        }

        if (forcedBreakpoint.value) {
            lg.value = forcedBreakpoint.value === 'lg'
            sm.value = forcedBreakpoint.value !== 'lg'
            initialized.value = true
            document.documentElement.setAttribute('data-breakpoint', lg.value ? 'lg' : 'sm')
            return
        }

        const val = getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-sm')
        const breakpoint = parseInt(val.trim()) || DEFAULT_SM_BREAKPOINT

        const containerWidth = widgetRef.value?.clientWidth || window.innerWidth

        initialized.value = true
        sm.value = containerWidth <= breakpoint
        lg.value = containerWidth > breakpoint

        document.documentElement.setAttribute('data-breakpoint', lg.value ? 'lg' : 'sm')
    }

    watch(forcedBreakpoint, () => update())

    watch(widgetRef, (newWidget, oldWidget) => {
        update()

        if (oldWidget) {
            resizeObserver.unobserve(oldWidget)
        }
        if (newWidget) {
            resizeObserver.observe(newWidget)
        }
    })

    onUnmounted(() => resizeObserver.disconnect())

    instance = { sm, lg, initialized, current }

    return { update }
}

export function useBreakpoints(): UseBreakpoints {
    if (!instance) {
        throw new Error('Breakpoints not installed. Please call installBreakpoints() in a root component.')
    }

    return instance!
}
